import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, Trash2, ShieldQuestion, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, API_BASE } from "@/lib/api";
import { hubByKey, fmtNum } from "@/lib/hubs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Header } from "@/pages/dashboard/Insights";

export const hdDownload = async (id) => {
  try { const { data } = await api.get(`/tracks/${id}/hd-url`); window.location.href = `${API_BASE}${data.url}`; } catch (e) { toast.error(errMsg(e)); }
};

const FlagBadge = ({ flag }) => {
  if (!flag || flag.status === "clear" || flag.status === "cleared") return <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 size={12} /> Tags OK</span>;
  if (flag.status === "review_requested") return <span className="text-xs text-sky-300">Human review pending</span>;
  if (flag.status === "reclassified") return <span className="text-xs text-zinc-400">Re-classified by moderation</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-amber-300"><AlertTriangle size={12} /> Tag flag</span>;
};

export const TrackManager = () => {
  const [tracks, setTracks] = useState([]);
  const [review, setReview] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/me/tracks").then((r) => setTracks(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => { try { await api.patch(`/tracks/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this track?")) return; await api.delete(`/tracks/${id}`); load(); };
  const sendReview = async () => {
    try { await api.post(`/tracks/${review.track_id}/request-review`, { message: msg }); toast.success("Manual verification requested — a human moderator will review."); setReview(null); setMsg(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="track-manager">
      <Header eyebrow="Catalog" title="Track manager" />
      <div className="surface divide-y divide-white/5">
        {tracks.length === 0 && <div className="p-8 text-zinc-500">No tracks yet. Head to Upload to publish your first one.</div>}
        {tracks.map((t) => (
          <div key={t.track_id} data-testid={`manager-row-${t.track_id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-4">
            <img src={mediaUrl(t.cover_url)} alt="" className="w-14 h-14 rounded-lg object-cover bg-zinc-800" />
            <div className="flex-1 min-w-0">
              <Link to={`/track/${t.track_id}`} className="font-semibold hover:underline">{t.title}</Link>
              <div className="text-xs text-zinc-500 mt-0.5">{t.genres.map((g) => hubByKey(g)?.name).join(" · ")}{t.subtag ? ` · ${t.subtag}` : ""} · {fmtNum(t.streams)} plays {t.status === "suspended" && <span className="text-red-400 ml-2">SUSPENDED</span>}{t.mastered && <span className="text-fuchsia-300 ml-2">Mastered</span>}</div>
              <div className="mt-1 flex items-center gap-3"><FlagBadge flag={t.tag_flag} />
                {t.tag_flag?.status === "flagged" && <button data-testid={`request-review-${t.track_id}`} onClick={() => setReview(t)} className="inline-flex items-center gap-1 text-xs font-semibold text-white underline underline-offset-2"><ShieldQuestion size={12} /> Request Manual Verification</button>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-xs text-zinc-400">Public <Switch data-testid={`privacy-switch-${t.track_id}`} checked={t.privacy === "public"} onCheckedChange={(v) => patch(t.track_id, { privacy: v ? "public" : "private" })} /></label>
              <button data-testid={`hd-download-${t.track_id}`} onClick={() => hdDownload(t.track_id)} className="text-zinc-400 hover:text-white" title="HD download (signed link, 5 min)"><Download size={16} /></button>
              <button data-testid={`delete-track-${t.track_id}`} onClick={() => del(t.track_id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="bg-[#12121A] border-white/10" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Request Manual Verification</DialogTitle>
            <DialogDescription>Right to human review (GDPR art. 22). Automated flags never remove your track; a moderator will check your tags.</DialogDescription></DialogHeader>
          <p className="text-sm text-amber-200">{review?.tag_flag?.issues?.join("; ")}</p>
          <Textarea data-testid="review-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Explain why your tags are correct (optional)" className="bg-white/5 border-white/10" />
          <button data-testid="review-submit-button" onClick={sendReview} className="h-11 rounded-full bg-white text-black font-semibold">Send to moderation</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
